import React from 'react';

/**
 * Off-screen printable portfolio view used for PDF export.
 * Uses inline styles so that html2canvas renders consistently
 * regardless of the visible page's Tailwind classes.
 */

const PAGE = {
    background: '#FBF8F3',
    color: '#1A1612',
    fontFamily:
        "'Instrument Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    width: '180mm', // fits A4 with 14mm margins
    minHeight: '265mm',
    padding: '0',
    fontSize: '10.5pt',
    lineHeight: 1.55,
    boxSizing: 'border-box',
};

const HEADING = {
    fontFamily: "'Fraunces', ui-serif, Georgia, serif",
    color: '#1A1612',
};

const PRIMARY = '#B14A2C'; // terracotta

const KICKER = {
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    fontSize: '8pt',
    color: '#6F665E',
    fontWeight: 500,
    margin: 0,
};

const RULE = { height: '1px', width: '28px', background: '#DDD2C5', margin: '6px 0 12px' };

function Section({ children, style }) {
    return (
        <section style={{ padding: '14mm 14mm 6mm', ...style }} className="pdf-avoid-break">
            {children}
        </section>
    );
}

function SampleRow({ sample }) {
    return (
        <div
            className="pdf-sample"
            style={{
                padding: '8px 0',
                borderTop: '1px solid #EADFD0',
                breakInside: 'avoid',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <p
                    style={{
                        ...HEADING,
                        margin: 0,
                        fontSize: '11pt',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        flex: 1,
                    }}
                >
                    {sample.title}
                </p>
                <span style={{ ...KICKER, whiteSpace: 'nowrap' }}>
                    {sample.language}
                </span>
            </div>
            {sample.excerpt && (
                <p
                    style={{
                        margin: '4px 0 0',
                        color: '#4A4038',
                        fontSize: '9.5pt',
                        lineHeight: 1.5,
                    }}
                >
                    {sample.excerpt}
                </p>
            )}
            {sample.externalUrl && (
                <p
                    style={{
                        margin: '4px 0 0',
                        color: PRIMARY,
                        fontSize: '8.5pt',
                        wordBreak: 'break-all',
                    }}
                >
                    {sample.externalUrl}
                </p>
            )}
        </div>
    );
}

const PortfolioPDF = React.forwardRef(function PortfolioPDF({ samples = [] }, ref) {
    // Group samples by content type, preserving stable order
    const ORDER = [
        'Blog',
        'Newsletter',
        'Press Release',
        'Website Copy',
        'Landing Page',
        'UI Microcopy',
        'Bilingual',
    ];
    const grouped = ORDER.map((type) => ({
        type,
        items: samples.filter((s) => s.contentType === type),
    })).filter((g) => g.items.length > 0);

    return (
        <div ref={ref} style={PAGE}>
            {/* ---------- Cover ---------- */}
            <section
                style={{
                    padding: '22mm 14mm 12mm',
                    borderBottom: '1px solid #EADFD0',
                    background:
                        'radial-gradient(600px circle at 85% 0%, rgba(177,74,44,0.12), transparent 60%), #FBF8F3',
                }}
                className="pdf-avoid-break"
            >
                <p style={KICKER}>Portfolio · 2026</p>
                <h1
                    style={{
                        ...HEADING,
                        fontSize: '34pt',
                        lineHeight: 1.02,
                        letterSpacing: '-0.02em',
                        margin: '6px 0 4px',
                    }}
                >
                    Words that work.
                    <br />
                    <span style={{ color: PRIMARY }}>In two languages.</span>
                </h1>
                <p
                    style={{
                        ...HEADING,
                        fontSize: '15pt',
                        margin: '16px 0 2px',
                    }}
                >
                    Pallabi Bose
                </p>
                <p style={{ margin: 0, fontSize: '10pt', color: '#6F665E' }}>
                    Content writer &amp; client-servicing expert · 11+ years · English / বাংলা
                </p>
                <p
                    style={{
                        margin: '14px 0 0',
                        maxWidth: '140mm',
                        fontSize: '10.5pt',
                        color: '#4A4038',
                        lineHeight: 1.6,
                    }}
                >
                    I write for the moment between intent and action — the
                    inbox at 9am, the empty cart at midnight, the press
                    release that lands at 6pm. Currently Sr. Product Content
                    Analyst at BankBazaar. Previously UI/UX content writer
                    for the Bengali Flipkart App.
                </p>
                <div style={{ marginTop: 18, fontSize: '9.5pt', color: '#4A4038' }}>
                    <div>✉  tinapallabi8@gmail.com</div>
                    <div>☎  +91 8240 888 646</div>
                    <div>↗  LinkedIn — Pallabi Bose</div>
                </div>
            </section>

            {/* ---------- About ---------- */}
            <Section>
                <p style={KICKER}>§ About</p>
                <div style={RULE} />
                <h2 style={{ ...HEADING, fontSize: '16pt', margin: '0 0 10px' }}>
                    The writer behind the desk.
                </h2>
                <p style={{ margin: '0 0 8px' }}>
                    I’ve spent the last decade in the rooms where content meets
                    product — currently as Sr. Product Content Analyst at
                    BankBazaar, writing across credit, savings and launch-stage
                    products for 50M+ users.
                </p>
                <p style={{ margin: '0 0 8px' }}>
                    Before BankBazaar, I spent four-and-a-half years writing
                    UI/UX copy for the Bengali Flipkart App, in-app content,
                    brand ads, video commerce, and Flipkart Grocery. Earlier,
                    I produced regional radio campaigns at Salt Audios, edited
                    indie manuscripts at Notion Press, covered tech hiring at
                    Fortune India, and wrote 50+ vendor profiles at Silicon
                    India’s CIO Review.
                </p>
                <p style={{ margin: 0 }}>
                    As a freelancer I’ve worked with 15+ B2B and B2C brands —
                    150+ blogs, 20+ press releases, 10+ website copies, zero
                    plagiarism. I write bilingually because Bengali is not my
                    translation target. It’s my source language too.
                </p>
            </Section>

            {/* ---------- Notable launches ---------- */}
            <Section style={{ paddingTop: '4mm' }}>
                <p style={KICKER}>§ Notable launches</p>
                <div style={RULE} />
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '10px',
                    }}
                >
                    <div
                        className="pdf-avoid-break"
                        style={{
                            border: '1px solid #EADFD0',
                            borderRadius: 10,
                            padding: '10px 12px',
                            background: '#FFFDF8',
                        }}
                    >
                        <p style={{ ...KICKER, fontSize: '7.5pt' }}>
                            Product launch
                        </p>
                        <p
                            style={{
                                ...HEADING,
                                margin: '4px 0 6px',
                                fontSize: '11.5pt',
                                fontWeight: 600,
                            }}
                        >
                            CreditStrong — Regional Language Platform
                        </p>
                        <p style={{ margin: 0, fontSize: '9.5pt', color: '#4A4038' }}>
                            Bootstrapped the regional-language launch end to
                            end — UI content plus promotional content across
                            WhatsApp, email and SMS. Shipped the Bengali
                            version first; served as content SPOC for the
                            rollout across 9 Indian languages. (CreditStrong
                            is a BankBazaar product.)
                        </p>
                    </div>
                    <div
                        className="pdf-avoid-break"
                        style={{
                            border: '1px solid #EADFD0',
                            borderRadius: 10,
                            padding: '10px 12px',
                            background: '#FFFDF8',
                        }}
                    >
                        <p style={{ ...KICKER, fontSize: '7.5pt' }}>
                            Flagship property
                        </p>
                        <p
                            style={{
                                ...HEADING,
                                margin: '4px 0 6px',
                                fontSize: '11.5pt',
                                fontWeight: 600,
                            }}
                        >
                            BankBazaar Money Moves — Newsletter
                        </p>
                        <p style={{ margin: 0, fontSize: '9.5pt', color: '#4A4038' }}>
                            Launched and continue to write the weekly
                            consumer-finance newsletter. 50+ issues shipped,
                            built specifically to reactivate a snoozing
                            subscriber base with sharp, useful takes on money.
                        </p>
                    </div>
                </div>
            </Section>

            {/* ---------- Clients ---------- */}
            <Section style={{ paddingTop: '4mm' }}>
                <p style={KICKER}>§ Brands I’ve written for</p>
                <div style={RULE} />
                <p style={{ margin: 0, fontSize: '10pt', color: '#4A4038' }}>
                    BankBazaar · Flipkart · Fortune India · Salt Audios ·
                    Notion Press · Xanadu · Jar · Design Cafe · Gizbot ·
                    Silicon India · Infiniti Research · Pest Shields · BizPay
                    · RPG Foundation · Rapido · Quick Heal Foundation ·
                    ToneTag · Okinawa Autotech · TCI · PSA Ameya · Vahak ·
                    Trefoil
                </p>
            </Section>

            {/* ---------- Selected work by category ---------- */}
            {grouped.map((group) => (
                <Section key={group.type} style={{ paddingTop: '4mm' }}>
                    <p style={KICKER}>§ {group.type}</p>
                    <div style={RULE} />
                    <div>
                        {group.items.map((s) => (
                            <SampleRow key={s.id} sample={s} />
                        ))}
                    </div>
                </Section>
            ))}

            {/* ---------- Footer ---------- */}
            <Section
                style={{
                    paddingTop: '6mm',
                    paddingBottom: '14mm',
                    borderTop: '1px solid #EADFD0',
                    marginTop: '4mm',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                    }}
                >
                    <p
                        style={{
                            ...HEADING,
                            margin: 0,
                            fontSize: '11pt',
                        }}
                    >
                        Pallabi Bose · Words that work. In two languages.
                    </p>
                    <p style={{ ...KICKER, margin: 0 }}>
                        © {new Date().getFullYear()}
                    </p>
                </div>
            </Section>
        </div>
    );
});

export default PortfolioPDF;
